import type {
  Point,
  SketchPointerEvent,
  SketchPointerType,
  SketchStrokePrimitive,
  SketchToolStyle,
  SketchToolType,
} from "../../../../../types/stage-types.js";
import { logger } from "../../../../../utils/logger.ts";
import type {
  StrokeRendererAPI,
  StrokeRenderPayload,
} from "./StrokeRenderer.js";
import {
  pointsToPath,
  pointsToSmoothPath,
  pointsToPolylinePath,
} from "./StrokeRenderer.js";

export interface SketchStrokeModel {
  readonly id: string;
  readonly pointerId: number;
  readonly pointerType: SketchPointerType;
  readonly tool: SketchToolType;
  readonly color: string;
  readonly width: number;
  readonly opacity: number;
  readonly splitFromId?: string;
  readonly primitive?: SketchStrokePrimitive;
  readonly points: readonly StrokePoint[];
  readonly pathData: string;
  readonly createdAt: number;
  readonly updatedAt: number;
}

export interface StrokePoint extends Point {
  readonly p?: number;
  readonly t?: number;
}

export interface ToolManagerConfig {
  readonly renderer: StrokeRendererAPI;
  readonly defaultColor?: string;
  readonly defaultWidth?: number;
  readonly defaultOpacity?: number;
  readonly getModifiers?: () => { readonly shift: boolean; readonly alt: boolean };
  readonly onStrokeCommit?: (stroke: SketchStrokeModel) => void;
  readonly onStrokeAdded?: (stroke: SketchStrokeModel) => void;
  readonly onStrokeRemoved?: (strokeId: string) => void;
  readonly onStrokeReplaced?: (stroke: SketchStrokeModel) => void;
  readonly onHistoryChange?: (state: {
    readonly canUndo: boolean;
    readonly canRedo: boolean;
  }) => void;
  readonly logDebug?: boolean;
  readonly maxHistory?: number;
}

export interface ToolManagerAPI {
  readonly handlePointerEvent: (event: SketchPointerEvent) => void;
  readonly setActive: (active: boolean) => void;
  readonly reset: () => void;
  readonly destroy: () => void;
  readonly getStrokes: () => readonly SketchStrokeModel[];
  readonly setStrokes: (strokes: readonly SketchStrokeModel[]) => void;
  readonly ingestStroke: (stroke: SketchStrokeModel) => void;
  readonly removeStroke: (strokeId: string) => void;
  readonly removeStrokes: (strokeIds: readonly string[]) => void;
  readonly getSelection: () => readonly SketchStrokeModel[];
  readonly setSelection: (selection: readonly SketchStrokeModel[]) => void;
  readonly previewStrokeUpdates: (strokes: readonly SketchStrokeModel[]) => void;
  readonly commitStrokeUpdates: (
    before: readonly SketchStrokeModel[],
    after: readonly SketchStrokeModel[],
    options?: { readonly emitEvents?: boolean }
  ) => void;
  readonly updateStrokes: (
    strokes: readonly SketchStrokeModel[]
  ) => void;
  readonly getStyle: () => SketchToolStyle;
  readonly setStyle: (style: Partial<SketchToolStyle>) => void;
  readonly undo: () => SketchStrokeModel | null;
  readonly redo: () => SketchStrokeModel | null;
  readonly canUndo: () => boolean;
  readonly canRedo: () => boolean;
  /** Force-finish any active stroke for the given pointer ID (used when cleaning up orphaned pointers) */
  readonly forceFinishStroke: (pointerId: number) => void;
}

interface StrokeDraft {
  readonly id: string;
  readonly pointerId: number;
  readonly pointerType: SketchPointerType;
  readonly tool: SketchToolType;
  readonly color: string;
  readonly width: number;
  readonly opacity: number;
  readonly origin: StrokePoint;
  readonly createdAt: number;
  points: StrokePoint[];
  updatedAt: number;
  lastSmoothed?: StrokePoint;
  primitive?: SketchStrokePrimitive;
  pathDataOverride?: string;
  shapeModifiers?: { readonly shift: boolean; readonly alt: boolean };
  shapeHoldStart?: number | null;
  shapeHoldOrigin?: StrokePoint | null;
  lastMoveAt?: number | null;
  lineHoldStart?: number | null;
  lineHoldOrigin?: StrokePoint | null;
  lineHoldTravel?: number | null;
  altAnchor?: "center" | "corner";
}

type HistoryAction =
  | {
      readonly type: "add" | "remove";
      readonly stroke: SketchStrokeModel;
      readonly index: number;
    }
  | {
      readonly type: "split";
      readonly stroke: SketchStrokeModel;
      readonly index: number;
      readonly segments: readonly SketchStrokeModel[];
    }
  | {
      readonly type: "update";
      readonly before: SketchStrokeModel;
      readonly after: SketchStrokeModel;
      readonly index: number;
    };

interface HistoryEntry {
  readonly type: "single" | "batch";
  readonly actions: readonly HistoryAction[];
}

const DEFAULT_COLOR = "#000000";
const DEFAULT_WIDTH = 4;
const DEFAULT_OPACITY = 1;
const DEFAULT_TOOL: SketchToolType = "ballpoint";
const DEFAULT_HISTORY_LIMIT = 50;
const MIN_DISTANCE_DELTA = 0.5;
const MAX_POINT_DISTANCE = 6;
const PEN_MIN_DISTANCE_DELTA = 0.4;
const PEN_MAX_POINT_DISTANCE = 4;
const HIGHLIGHTER_DISTANCE_MULTIPLIER = 1.5;
const PEN_SMOOTHING_MIN_ALPHA = 0.12;
const PEN_SMOOTHING_MAX_ALPHA = 0.6;
const PEN_SMOOTHING_DISTANCE_SCALE = 8;
const SEGMENT_GAP_MS = 120;
const PEN_TAP_JITTER = 0.8;
const SHAPE_HOLD_MS = 600;
const SHAPE_HOLD_MAX_DRIFT = 3;
const LINE_HOLD_MS = 500;
const LINE_HOLD_MAX_DRIFT = 3;
const LINE_HOLD_MAX_TRAVEL = 6;
const END_HOLD_MS = 450;
const END_HOLD_DRIFT = 3;
const CLOSED_SHAPE_DISTANCE = 12;
const RECT_EDGE_TOLERANCE = 6;
const ELLIPSE_VARIANCE_TOLERANCE = 0.25;
const SHAPE_POINT_SPACING = 4;
const SHAPE_MAX_POINTS = 240;

const resolveModifierState = (
  event: SketchPointerEvent,
  getModifiers?: ToolManagerConfig["getModifiers"]
): { shift: boolean; alt: boolean } => {
  const fromUI = getModifiers?.() ?? { shift: false, alt: false };
  const original = event.originalEvent;
  return {
    shift: Boolean(fromUI.shift || original?.shiftKey),
    alt: Boolean(fromUI.alt || original?.altKey),
  };
};

const clampWidth = (value: number): number =>
  Number.isFinite(value) ? Math.max(0.5, Math.min(64, value)) : DEFAULT_WIDTH;

const resolveTool = (value: unknown): SketchToolType | null => {
  if (
    value === "ballpoint" ||
    value === "highlighter" ||
    value === "eraser" ||
    value === "marquee"
  ) {
    return value;
  }
  return null;
};

const clonePoint = (point: StrokePoint): StrokePoint => ({
  x: point.x,
  y: point.y,
  ...(typeof point.p === "number" ? { p: point.p } : {}),
  ...(typeof point.t === "number" ? { t: point.t } : {}),
});

const distanceSquared = (a: Point, b: Point): number => {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  return dx * dx + dy * dy;
};

const hasSignificantDelta = (a: Point, b: Point): boolean =>
  distanceSquared(a, b) > 0.01;

const appendPointWithInterpolation = (
  points: StrokePoint[],
  nextPoint: StrokePoint,
  thresholds: {
    readonly minDistanceSq: number;
    readonly maxDistanceSq: number;
    readonly maxDistance: number;
  }
): void => {
  const lastPoint = points[points.length - 1];
  if (!lastPoint) {
    points.push(nextPoint);
    return;
  }

  const deltaSq = distanceSquared(lastPoint, nextPoint);
  if (deltaSq < thresholds.minDistanceSq) {
    points[points.length - 1] = nextPoint;
    return;
  }

  if (deltaSq <= thresholds.maxDistanceSq) {
    points.push(nextPoint);
    return;
  }

  const distance = Math.sqrt(deltaSq);
  const steps = Math.ceil(distance / thresholds.maxDistance);
  for (let step = 1; step <= steps; step += 1) {
    const t = step / steps;
    points.push({
      x: lastPoint.x + (nextPoint.x - lastPoint.x) * t,
      y: lastPoint.y + (nextPoint.y - lastPoint.y) * t,
    });
  }
};

const getPenSmoothingAlpha = (distance: number): number => {
  const normalized = Math.min(
    1,
    Math.max(0, distance / PEN_SMOOTHING_DISTANCE_SCALE)
  );
  return (
    PEN_SMOOTHING_MIN_ALPHA +
    (PEN_SMOOTHING_MAX_ALPHA - PEN_SMOOTHING_MIN_ALPHA) * normalized
  );
};

const smoothPenPoint = (
  draft: StrokeDraft,
  rawPoint: StrokePoint
): StrokePoint => {
  if (!draft.lastSmoothed) {
    const seed = clonePoint(rawPoint);
    draft.lastSmoothed = seed;
    return seed;
  }

  const dx = rawPoint.x - draft.lastSmoothed.x;
  const dy = rawPoint.y - draft.lastSmoothed.y;
  const distance = Math.sqrt(dx * dx + dy * dy);
  const alpha = getPenSmoothingAlpha(distance);

  const next: StrokePoint = {
    x: draft.lastSmoothed.x + dx * alpha,
    y: draft.lastSmoothed.y + dy * alpha,
    ...(typeof rawPoint.p === "number" ? { p: rawPoint.p } : {}),
    ...(typeof rawPoint.t === "number" ? { t: rawPoint.t } : {}),
  };

  draft.lastSmoothed = next;
  return next;
};

const createStrokePayload = (
  draft: StrokeDraft,
  pathData: string
): StrokeRenderPayload => ({
  id: draft.id,
  pathData,
  tool: draft.tool,
  color: draft.color,
  width: draft.width,
  opacity: draft.opacity,
});

const createPayloadFromStroke = (
  stroke: SketchStrokeModel
): StrokeRenderPayload => ({
  id: stroke.id,
  pathData: stroke.pathData,
  tool: stroke.tool,
  color: stroke.color,
  width: stroke.width,
  opacity: stroke.opacity,
});

const buildLinePoints = (
  start: Point,
  end: Point,
  spacing: number
): StrokePoint[] => {
  const dx = end.x - start.x;
  const dy = end.y - start.y;
  const distance = Math.hypot(dx, dy);
  const steps = Math.max(1, Math.ceil(distance / spacing));
  const points: StrokePoint[] = [];
  for (let step = 0; step <= steps; step += 1) {
    const t = step / steps;
    points.push({
      x: start.x + dx * t,
      y: start.y + dy * t,
    });
  }
  return points;
};

const snapPointToAngle = (
  origin: Point,
  point: Point,
  stepRadians: number
): Point => {
  const dx = point.x - origin.x;
  const dy = point.y - origin.y;
  const distance = Math.hypot(dx, dy);
  if (distance === 0) return { x: origin.x, y: origin.y };
  const angle = Math.atan2(dy, dx);
  const snapped = Math.round(angle / stepRadians) * stepRadians;
  return {
    x: origin.x + Math.cos(snapped) * distance,
    y: origin.y + Math.sin(snapped) * distance,
  };
};

const buildRectPoints = (
  minX: number,
  minY: number,
  maxX: number,
  maxY: number,
  spacing: number
): StrokePoint[] => {
  const corners: Point[] = [
    { x: minX, y: minY },
    { x: maxX, y: minY },
    { x: maxX, y: maxY },
    { x: minX, y: maxY },
    { x: minX, y: minY },
  ];
  const points: StrokePoint[] = [];
  for (let index = 0; index < corners.length - 1; index += 1) {
    const start = corners[index]!;
    const end = corners[index + 1]!;
    const segment = buildLinePoints(start, end, spacing);
    if (index > 0 && segment.length > 0) {
      segment.shift();
    }
    points.push(...segment);
  }
  return points.slice(0, SHAPE_MAX_POINTS);
};

const buildEllipsePoints = (
  cx: number,
  cy: number,
  rx: number,
  ry: number,
  spacing: number
): StrokePoint[] => {
  if (rx <= 0 || ry <= 0) {
    return [{ x: cx, y: cy }];
  }
  const circumference =
    Math.PI *
    (3 * (rx + ry) - Math.sqrt((3 * rx + ry) * (rx + 3 * ry)));
  const count = Math.min(
    SHAPE_MAX_POINTS,
    Math.max(12, Math.ceil(circumference / spacing))
  );
  const points: StrokePoint[] = [];
  for (let i = 0; i <= count; i += 1) {
    const angle = (i / count) * Math.PI * 2;
    points.push({
      x: cx + Math.cos(angle) * rx,
      y: cy + Math.sin(angle) * ry,
    });
  }
  return points;
};

const estimateClosedShape = (
  points: readonly StrokePoint[]
): {
  primitive: SketchStrokePrimitive;
  points: StrokePoint[];
  pathData: string;
} | null => {
  if (points.length < 8) return null;
  const first = points[0]!;
  const last = points[points.length - 1]!;
  const closeDx = first.x - last.x;
  const closeDy = first.y - last.y;
  if (Math.hypot(closeDx, closeDy) > CLOSED_SHAPE_DISTANCE) return null;

  let minX = first.x;
  let maxX = first.x;
  let minY = first.y;
  let maxY = first.y;
  for (const point of points) {
    minX = Math.min(minX, point.x);
    maxX = Math.max(maxX, point.x);
    minY = Math.min(minY, point.y);
    maxY = Math.max(maxY, point.y);
  }
  const width = maxX - minX;
  const height = maxY - minY;
  if (width <= 0 || height <= 0) return null;

  const cx = (minX + maxX) / 2;
  const cy = (minY + maxY) / 2;
  const rx = width / 2;
  const ry = height / 2;
  let varianceSum = 0;
  for (const point of points) {
    const nx = (point.x - cx) / rx;
    const ny = (point.y - cy) / ry;
    const radius = Math.hypot(nx, ny);
    varianceSum += Math.abs(1 - radius);
  }
  const varianceAvg = varianceSum / points.length;
  let edgeDistanceSum = 0;
  for (const point of points) {
    const edgeDistance = Math.min(
      Math.abs(point.x - minX),
      Math.abs(point.x - maxX),
      Math.abs(point.y - minY),
      Math.abs(point.y - maxY)
    );
    edgeDistanceSum += edgeDistance;
  }
  const edgeDistanceAvg = edgeDistanceSum / points.length;
  const rectScore = edgeDistanceAvg / Math.max(width, height);
  const ellipseScore = varianceAvg;

  if (
    ellipseScore <= ELLIPSE_VARIANCE_TOLERANCE &&
    (ellipseScore <= rectScore * 0.85 || rectScore > RECT_EDGE_TOLERANCE / Math.max(width, height))
  ) {
    const isCircle = Math.abs(width - height) / Math.max(width, height) < 0.2;
    const ellipsePoints = buildEllipsePoints(cx, cy, rx, ry, SHAPE_POINT_SPACING);
    return {
      primitive: isCircle ? "circle" : "ellipse",
      points: ellipsePoints,
      pathData: buildEllipsePath(cx, cy, rx, ry),
    };
  }

  if (edgeDistanceAvg <= RECT_EDGE_TOLERANCE) {
    const isSquare = Math.abs(width - height) / Math.max(width, height) < 0.2;
    const rectPoints = buildRectPoints(
      minX,
      minY,
      maxX,
      maxY,
      SHAPE_POINT_SPACING
    );
    return {
      primitive: isSquare ? "square" : "rect",
      points: rectPoints,
      pathData: buildRectPath(minX, minY, maxX, maxY),
    };
  }

  return null;
};

const isClosedStroke = (points: readonly StrokePoint[]): boolean => {
  if (points.length < 4) return false;
  const first = points[0]!;
  const last = points[points.length - 1]!;
  return Math.hypot(first.x - last.x, first.y - last.y) <= CLOSED_SHAPE_DISTANCE;
};

const buildRectPath = (
  minX: number,
  minY: number,
  maxX: number,
  maxY: number
): string =>
  `M ${minX} ${minY} L ${maxX} ${minY} L ${maxX} ${maxY} L ${minX} ${maxY} Z`;

const buildEllipsePath = (
  cx: number,
  cy: number,
  rx: number,
  ry: number
): string => {
  if (rx <= 0 || ry <= 0) {
    return `M ${cx} ${cy} L ${cx} ${cy}`;
  }
  return `M ${cx + rx} ${cy} A ${rx} ${ry} 0 1 0 ${cx - rx} ${cy} A ${rx} ${ry} 0 1 0 ${cx + rx} ${cy}`;
};

export const createToolManager = (
  config: ToolManagerConfig
): ToolManagerAPI => {
  const renderer = config.renderer;
  const committedStrokes: SketchStrokeModel[] = [];
  const logDebug = config.logDebug ?? false;
  const historyLimit =
    typeof config.maxHistory === "number" && config.maxHistory > 0
      ? Math.floor(config.maxHistory)
      : DEFAULT_HISTORY_LIMIT;
  const undoStack: HistoryEntry[] = [];
  const redoStack: HistoryEntry[] = [];
  let selectedStrokes: SketchStrokeModel[] = [];
  const emitHistoryChange = (): void => {
    config.onHistoryChange?.({
      canUndo: undoStack.length > 0,
      canRedo: redoStack.length > 0,
    });
  };
  const pushUndo = (entry: HistoryEntry): void => {
    undoStack.push(entry);
    if (undoStack.length > historyLimit) {
      undoStack.shift();
    }
  };

  let currentStyle: SketchToolStyle = {
    color: config.defaultColor ?? DEFAULT_COLOR,
    width: clampWidth(config.defaultWidth ?? DEFAULT_WIDTH),
    opacity: config.defaultOpacity ?? DEFAULT_OPACITY,
    tool: DEFAULT_TOOL,
    eraserMode: "split",
    selectionMode: "marquee",
  };

  let isActive = true;
  let activeStroke: StrokeDraft | null = null;
  let activeEraserPointerId: number | null = null;
  let pendingEraserActions: HistoryAction[] = [];
  let eraserClearedRedo = false;
  let lastEraserPosition: Point | null = null;
  let eraserSourceStrokes: SketchStrokeModel[] | null = null;
  let eraserPoints: StrokePoint[] = [];
  let lastStrokeEndTime = 0;
  let lineChainAnchor: StrokePoint | null = null;

  const getEraserRadius = (): number =>
    Math.max(4, clampWidth(currentStyle.width ?? DEFAULT_WIDTH) / 2);

  const updateEraserCursor = (position: Point | null): void => {
    lastEraserPosition = position;
    if (!position) {
      renderer.setEraserCursor(null);
      return;
    }
    renderer.setEraserCursor({
      x: position.x,
      y: position.y,
      radius: getEraserRadius(),
    });
  };

  const getPointerThresholds = (
    pointerType: SketchPointerType,
    tool: SketchToolType
  ): { minDistanceSq: number; maxDistanceSq: number; maxDistance: number } =>
    (() => {
      const scale = tool === "highlighter" ? HIGHLIGHTER_DISTANCE_MULTIPLIER : 1;
      const minDistance =
        (pointerType === "pen" ? PEN_MIN_DISTANCE_DELTA : MIN_DISTANCE_DELTA) *
        scale;
      const maxDistance =
        (pointerType === "pen" ? PEN_MAX_POINT_DISTANCE : MAX_POINT_DISTANCE) *
        scale;
      return {
        minDistanceSq: minDistance * minDistance,
        maxDistanceSq: maxDistance * maxDistance,
        maxDistance,
      };
    })();

  const getEventPositions = (
    event: SketchPointerEvent
  ): readonly StrokePoint[] => {
    const toStrokePoint = (position: Point): StrokePoint => {
      const point: StrokePoint = { x: position.x, y: position.y };
      if (Number.isFinite(event.pressure)) point.p = event.pressure;
      const timeStamp = event.originalEvent?.timeStamp;
      if (typeof timeStamp === "number" && Number.isFinite(timeStamp)) {
        point.t = timeStamp;
      }
      return point;
    };

    const positions = event.coalescedPositions
      ? event.coalescedPositions.map(toStrokePoint)
      : [];
    const last = positions[positions.length - 1];
    if (!last || hasSignificantDelta(last, event.position)) {
      positions.push(toStrokePoint(event.position));
    }
    return positions;
  };

  const applyEventPositions = (
    draft: StrokeDraft,
    event: SketchPointerEvent
  ): void => {
    const positions = getEventPositions(event);
    const thresholds = getPointerThresholds(draft.pointerType, draft.tool);
    for (const position of positions) {
      const next =
        draft.pointerType === "pen"
          ? smoothPenPoint(draft, position)
          : clonePoint(position);
      appendPointWithInterpolation(draft.points, next, thresholds);
    }
  };

  const buildShapeForDraft = (
    draft: StrokeDraft,
    event: SketchPointerEvent,
    now: number
  ): {
    points: StrokePoint[];
    pathData: string;
    primitive: SketchStrokePrimitive;
    holdStart: number | null;
    holdOrigin: StrokePoint | null;
  } | null => {
    if (!draft.shapeModifiers) return null;
    const { shift, alt } = draft.shapeModifiers;
    if (!shift && !alt) return null;

    let holdStart = draft.shapeHoldStart ?? null;
    let holdOrigin = draft.shapeHoldOrigin ?? null;
    const wasEllipse =
      draft.primitive === "ellipse" ||
      draft.primitive === "circle";
    let useEllipse = wasEllipse;
    if (alt && !useEllipse) {
      if (!holdOrigin) {
        holdOrigin = { x: event.position.x, y: event.position.y };
        holdStart = now;
      } else {
        const dx = event.position.x - holdOrigin.x;
        const dy = event.position.y - holdOrigin.y;
        const driftSq = dx * dx + dy * dy;
        if (driftSq > SHAPE_HOLD_MAX_DRIFT * SHAPE_HOLD_MAX_DRIFT) {
          holdOrigin = { x: event.position.x, y: event.position.y };
          holdStart = now;
        }
      }
      if (holdStart !== null && now - holdStart >= SHAPE_HOLD_MS) {
        useEllipse = true;
      }
    } else if (!alt) {
      holdStart = null;
      holdOrigin = null;
    }

    const origin = draft.origin;
    const current = event.position;

    const lineMode = shift && (!alt || draft.primitive === "line");
    if (lineMode) {
      const lineEnd = alt
        ? snapPointToAngle(origin, current, Math.PI / 4)
        : current;
      const points = buildLinePoints(origin, lineEnd, SHAPE_POINT_SPACING);
      return {
        points,
        pathData: `M ${origin.x} ${origin.y} L ${lineEnd.x} ${lineEnd.y}`,
        primitive: "line",
        holdStart,
        holdOrigin,
      };
    }

    const altAnchor = draft.altAnchor ?? "center";
    let minX: number;
    let maxX: number;
    let minY: number;
    let maxY: number;
    if (altAnchor === "corner") {
      const dx = current.x - origin.x;
      const dy = current.y - origin.y;
      if (shift) {
        const size = Math.max(Math.abs(dx), Math.abs(dy));
        const sx = dx === 0 ? 1 : Math.sign(dx);
        const sy = dy === 0 ? 1 : Math.sign(dy);
        const x1 = origin.x + sx * size;
        const y1 = origin.y + sy * size;
        minX = Math.min(origin.x, x1);
        maxX = Math.max(origin.x, x1);
        minY = Math.min(origin.y, y1);
        maxY = Math.max(origin.y, y1);
      } else {
        minX = Math.min(origin.x, current.x);
        maxX = Math.max(origin.x, current.x);
        minY = Math.min(origin.y, current.y);
        maxY = Math.max(origin.y, current.y);
      }
    } else {
      let halfWidth = Math.abs(current.x - origin.x);
      let halfHeight = Math.abs(current.y - origin.y);
      if (shift) {
        const maxSide = Math.max(halfWidth, halfHeight);
        halfWidth = maxSide;
        halfHeight = maxSide;
      }
      minX = origin.x - halfWidth;
      maxX = origin.x + halfWidth;
      minY = origin.y - halfHeight;
      maxY = origin.y + halfHeight;
    }

    if (useEllipse) {
      const rx = Math.max(0, (maxX - minX) / 2);
      const ry = Math.max(0, (maxY - minY) / 2);
      const cx = (minX + maxX) / 2;
      const cy = (minY + maxY) / 2;
      const points = buildEllipsePoints(cx, cy, rx, ry, SHAPE_POINT_SPACING);
      return {
        points,
        pathData: buildEllipsePath(cx, cy, rx, ry),
        primitive: shift ? "circle" : "ellipse",
        holdStart,
        holdOrigin,
      };
    }

    const points = buildRectPoints(minX, minY, maxX, maxY, SHAPE_POINT_SPACING);
    return {
      points,
      pathData: buildRectPath(minX, minY, maxX, maxY),
      primitive: shift ? "square" : "rect",
      holdStart,
      holdOrigin,
    };
  };

  const applyStyleUpdate = (style: Partial<SketchToolStyle>): void => {
    const next: SketchToolStyle = {
      ...currentStyle,
      ...style,
    };

    const resolvedTool = resolveTool(next.tool);
    currentStyle = {
      color:
        typeof next.color === "string" && next.color.trim().length > 0
          ? next.color
          : currentStyle.color,
      width:
        typeof next.width === "number"
          ? clampWidth(next.width)
          : currentStyle.width,
      opacity:
        typeof next.opacity === "number"
          ? Math.max(0, Math.min(1, next.opacity))
          : currentStyle.opacity,
      tool: resolvedTool ?? currentStyle.tool ?? DEFAULT_TOOL,
      eraserMode:
        next.eraserMode === "clean" || next.eraserMode === "split"
          ? next.eraserMode
          : currentStyle.eraserMode ?? "split",
      selectionMode:
        next.selectionMode === "lasso" || next.selectionMode === "marquee"
          ? next.selectionMode
          : currentStyle.selectionMode ?? "marquee",
    };

    if (currentStyle.tool !== "eraser" && activeEraserPointerId !== null) {
      activeEraserPointerId = null;
      pendingEraserActions = [];
      eraserClearedRedo = false;
      updateEraserCursor(null);
    }
    if (currentStyle.tool === "eraser" && lastEraserPosition) {
      updateEraserCursor(lastEraserPosition);
    }
  };

  const applyStrokeAdd = (
    stroke: SketchStrokeModel,
    options: {
      recordHistory: boolean;
      clearRedo: boolean;
      emitHistory?: boolean;
      emitEvent?: boolean;
      insertIndex?: number;
    }
  ): void => {
    const insertIndex =
      typeof options.insertIndex === "number"
        ? Math.min(
            Math.max(0, Math.floor(options.insertIndex)),
            committedStrokes.length
          )
        : null;

    if (insertIndex === null || insertIndex === committedStrokes.length) {
      committedStrokes.push(stroke);
      renderer.commitStroke(createPayloadFromStroke(stroke));
    } else {
      committedStrokes.splice(insertIndex, 0, stroke);
      let beforeId: string | null = null;
      for (let index = insertIndex + 1; index < committedStrokes.length; index += 1) {
        const next = committedStrokes[index]!;
        if (next.tool === stroke.tool) {
          beforeId = next.id;
          break;
        }
      }
      renderer.insertStroke(createPayloadFromStroke(stroke), beforeId);
    }

    if (options.recordHistory) {
      pushUndo({
        type: "single",
        actions: [
          {
            type: "add",
            stroke,
            index: insertIndex ?? committedStrokes.length - 1,
          },
        ],
      });
      if (options.clearRedo) {
        redoStack.length = 0;
      }
    }
    if (options.emitHistory !== false) {
      emitHistoryChange();
    }
    if (options.emitEvent !== false) {
      config.onStrokeAdded?.(stroke);
    }
  };

  const applyStrokeRemove = (
    stroke: SketchStrokeModel,
    options: {
      recordHistory: boolean;
      clearRedo: boolean;
      emitHistory?: boolean;
      indexOverride?: number;
      emitEvent?: boolean;
    }
  ): number => {
    renderer.removeStroke(stroke.id);
    const index =
      typeof options.indexOverride === "number"
        ? (() => {
            const candidate = options.indexOverride;
            if (
              candidate >= 0 &&
              candidate < committedStrokes.length &&
              committedStrokes[candidate]?.id === stroke.id
            ) {
              return candidate;
            }
            return committedStrokes.findIndex((item) => item.id === stroke.id);
          })()
        : committedStrokes.findIndex((item) => item.id === stroke.id);
    if (index >= 0) {
      committedStrokes.splice(index, 1);
    }
    const safeIndex = index >= 0 ? index : 0;
    if (options.recordHistory) {
      pushUndo({
        type: "single",
        actions: [
          {
            type: "remove",
            stroke,
            index: safeIndex,
          },
        ],
      });
      if (options.clearRedo) {
        redoStack.length = 0;
      }
    }
    if (options.emitHistory !== false) {
      emitHistoryChange();
    }
    if (options.emitEvent !== false) {
      config.onStrokeRemoved?.(stroke.id);
    }
    return safeIndex;
  };

  const applyStrokeUpdate = (
    before: SketchStrokeModel,
    after: SketchStrokeModel,
    options: {
      recordHistory: boolean;
      clearRedo: boolean;
      emitHistory?: boolean;
      indexOverride?: number;
      emitEvent?: boolean;
    }
  ): void => {
    const index =
      typeof options.indexOverride === "number"
        ? options.indexOverride
        : committedStrokes.findIndex((item) => item.id === before.id);
    if (index < 0) return;
    committedStrokes[index] = after;
    renderer.updateStroke(createPayloadFromStroke(after));

    if (selectedStrokes.length > 0) {
      selectedStrokes = selectedStrokes.map((stroke) =>
        stroke.id === after.id ? after : stroke
      );
    }

    if (options.recordHistory) {
      pushUndo({
        type: "single",
        actions: [
          {
            type: "update",
            before,
            after,
            index,
          },
        ],
      });
      if (options.clearRedo) {
        redoStack.length = 0;
      }
    }
    if (options.emitHistory !== false) {
      emitHistoryChange();
    }
    if (options.emitEvent !== false) {
      config.onStrokeReplaced?.(after);
    }
  };

  const strokePointIsErased = (
    point: StrokePoint,
    eraserPoints: readonly StrokePoint[],
    thresholdSq: number
  ): boolean => {
    for (const eraserPoint of eraserPoints) {
      if (distanceSquared(point, eraserPoint) <= thresholdSq) {
        return true;
      }
    }
    return false;
  };

  const splitStrokeByEraser = (
    stroke: SketchStrokeModel,
    eraserPoints: readonly StrokePoint[],
    radius: number
  ): { readonly segments: readonly StrokePoint[][]; readonly erased: boolean } => {
    const threshold = radius + stroke.width / 2;
    const thresholdSq = threshold * threshold;
    const segments: StrokePoint[][] = [];
    let current: StrokePoint[] = [];
    let erased = false;

    for (const point of stroke.points) {
      if (strokePointIsErased(point, eraserPoints, thresholdSq)) {
        if (current.length > 0) {
          segments.push(current);
          current = [];
        }
        erased = true;
        continue;
      }
      current.push(point);
    }

    if (current.length > 0) {
      segments.push(current);
    }

    return { segments, erased };
  };

  const buildSegmentStroke = (
    base: SketchStrokeModel,
    points: readonly StrokePoint[]
  ): SketchStrokeModel => ({
    ...base,
    id: createStrokeId(),
    splitFromId: base.splitFromId ?? base.id,
    primitive: undefined,
    points: points.slice(),
    pathData:
      base.pointerType === "pen"
        ? pointsToSmoothPath(points)
        : pointsToPath(points),
    createdAt: performance.now?.() ?? Date.now(),
    updatedAt: performance.now?.() ?? Date.now(),
  });

  const cloneStroke = (stroke: SketchStrokeModel): SketchStrokeModel => ({
    ...stroke,
    points: stroke.points.map(clonePoint),
  });

  const computeEraserResult = (
    source: readonly SketchStrokeModel[],
    positions: readonly StrokePoint[]
  ): { actions: HistoryAction[]; nextStrokes: SketchStrokeModel[] } => {
    const radius = getEraserRadius();
    const eraseMode = currentStyle.eraserMode ?? "split";
    const actions: HistoryAction[] = [];
    const nextStrokes: SketchStrokeModel[] = [];

    source.forEach((stroke, index) => {
      const { segments, erased } = splitStrokeByEraser(
        stroke,
        positions,
        radius
      );
      if (!erased) {
        nextStrokes.push(stroke);
        return;
      }

      if (eraseMode === "clean") {
        actions.push({ type: "remove", stroke, index });
        return;
      }

      const segmentStrokes =
        segments.length > 0
          ? segments.map((segment) => buildSegmentStroke(stroke, segment))
          : [];
      actions.push({
        type: "split",
        stroke,
        index,
        segments: segmentStrokes,
      });
      nextStrokes.push(...segmentStrokes);
    });

    return { actions, nextStrokes };
  };

  const eraseAtPositions = (
    positions: readonly StrokePoint[],
    options?: {
      recordHistory?: boolean;
      accumulate?: HistoryAction[];
      emitEvents?: boolean;
    }
  ): HistoryAction[] => {
    if (!positions.length || committedStrokes.length === 0) return [];
    const radius = getEraserRadius();
    const snapshot = committedStrokes.slice();
    const actions: HistoryAction[] = [];
    const eraseMode = currentStyle.eraserMode ?? "split";

    for (const stroke of snapshot) {
      const { segments, erased } = splitStrokeByEraser(
        stroke,
        positions,
        radius
      );
      if (!erased) continue;

      const index = committedStrokes.findIndex((item) => item.id === stroke.id);
      if (eraseMode === "clean") {
        applyStrokeRemove(stroke, {
          recordHistory: false,
          clearRedo: false,
          emitHistory: false,
          emitEvent: options?.emitEvents !== false,
          indexOverride: index,
        });
        actions.push({
          type: "remove",
          stroke,
          index,
        });
        continue;
      }

      const segmentStrokes =
        segments.length > 0
          ? segments.map((segment) => buildSegmentStroke(stroke, segment))
          : [];

      applyStrokeRemove(stroke, {
        recordHistory: false,
        clearRedo: false,
        emitHistory: false,
        emitEvent: options?.emitEvents !== false,
        indexOverride: index,
      });

      segmentStrokes.forEach((segmentStroke, offset) => {
        applyStrokeAdd(segmentStroke, {
          recordHistory: false,
          clearRedo: false,
          emitHistory: false,
          emitEvent: options?.emitEvents !== false,
          insertIndex: index + offset,
        });
      });

      actions.push({
        type: "split",
        stroke,
        index,
        segments: segmentStrokes,
      });
    }

    if (actions.length > 0) {
      options?.accumulate?.push(...actions);
      if (options?.recordHistory ?? true) {
        pushUndo({ type: "batch", actions });
        redoStack.length = 0;
        emitHistoryChange();
      }
    }

    return actions;
  };

  const commitDraft = (draft: StrokeDraft): void => {
    const pathData =
      draft.pathDataOverride ??
      (draft.pointerType === "pen"
        ? pointsToSmoothPath(draft.points)
        : pointsToPath(draft.points));
    if (!pathData) return;

    const strokeModel: SketchStrokeModel = {
      id: draft.id,
      pointerId: draft.pointerId,
      pointerType: draft.pointerType,
      tool: draft.tool,
      color: draft.color,
      width: draft.width,
      opacity: draft.opacity,
      ...(draft.primitive ? { primitive: draft.primitive } : {}),
      points: draft.points.map(clonePoint),
      pathData,
      createdAt: draft.createdAt,
      updatedAt: draft.updatedAt,
    };

    if (
      draft.primitive === "line" &&
      draft.shapeModifiers?.shift &&
      lineChainAnchor &&
      committedStrokes.length > 0
    ) {
      const previous = committedStrokes[committedStrokes.length - 1]!;
      const canMerge =
        previous.primitive === "line" &&
        previous.tool === strokeModel.tool &&
        previous.color === strokeModel.color &&
        previous.width === strokeModel.width &&
        previous.opacity === strokeModel.opacity;
      if (canMerge) {
        const previousPoints = previous.points.map(clonePoint);
        const nextPoints = strokeModel.points.slice();
        if (nextPoints.length > 0) {
          const first = nextPoints[0]!;
          const lastPrev = previousPoints[previousPoints.length - 1]!;
          if (distanceSquared(first, lastPrev) < 0.01) {
            nextPoints.shift();
          }
        }
        const mergedPoints = previousPoints.concat(nextPoints);
        const mergedPath = pointsToPolylinePath(mergedPoints, false);
        const mergedStroke: SketchStrokeModel = {
          ...previous,
          points: mergedPoints,
          pathData: mergedPath,
          updatedAt: draft.updatedAt,
        };
        applyStrokeUpdate(previous, mergedStroke, {
          recordHistory: true,
          clearRedo: true,
        });
        const lastPoint = mergedPoints[mergedPoints.length - 1];
        lineChainAnchor = lastPoint ? clonePoint(lastPoint) : null;
        return;
      }
    }

    if (draft.primitive === "line" && draft.shapeModifiers?.shift) {
      const lastPoint = draft.points[draft.points.length - 1];
      lineChainAnchor = lastPoint ? clonePoint(lastPoint) : lineChainAnchor;
    } else {
      lineChainAnchor = null;
    }

    applyStrokeAdd(strokeModel, { recordHistory: true, clearRedo: true });
    config.onStrokeCommit?.(strokeModel);

    if (logDebug) {
      logger.debug("sketch", "🖊️ Stroke committed", {
        id: strokeModel.id,
        points: strokeModel.points.length,
        pointerType: strokeModel.pointerType,
        tool: strokeModel.tool,
        width: strokeModel.width,
        opacity: strokeModel.opacity,
      });
    }
  };

  const cancelActiveStroke = (): void => {
    if (!activeStroke) return;

    if (logDebug) {
      logger.debug("sketch", "↩️ Stroke cancelled", {
        id: activeStroke.id,
        pointerId: activeStroke.pointerId,
      });
    }

    renderer.renderActiveStroke(null);
    activeStroke = null;
  };

  const startStroke = (event: SketchPointerEvent): void => {
    // Cancel lingering stroke from previous pointer (defensive)
    if (activeStroke) {
      const draft = activeStroke;
      activeStroke = null;
      renderer.renderActiveStroke(null);
      commitDraft(draft);
    }

    const now = performance.now?.() ?? Date.now();
    const positions = getEventPositions(event);
    const modifiers = resolveModifierState(event, config.getModifiers);
    const lineChainStart =
      modifiers.shift && lineChainAnchor ? clonePoint(lineChainAnchor) : null;
    const firstPosition = lineChainStart ?? positions[0] ?? event.position;
    const shouldForceSegment =
      event.pointerType === "pen" && now - lastStrokeEndTime <= SEGMENT_GAP_MS;
    const draft: StrokeDraft = {
      id: createStrokeId(),
      pointerId: event.pointerId,
      pointerType: event.pointerType,
      tool: currentStyle.tool ?? DEFAULT_TOOL,
      color: currentStyle.color,
      width: currentStyle.width,
      opacity: currentStyle.opacity ?? DEFAULT_OPACITY,
      origin: clonePoint(firstPosition),
      points: [clonePoint(firstPosition)],
      createdAt: now,
      updatedAt: now,
      lastMoveAt: now,
      ...(event.pointerType === "pen"
        ? {
            lastSmoothed: clonePoint(firstPosition),
            lineHoldStart: now,
            lineHoldOrigin: clonePoint(firstPosition),
            lineHoldTravel: 0,
          }
        : {}),
    };

    if (modifiers.shift || modifiers.alt) {
      draft.shapeModifiers = modifiers;
      if (modifiers.alt) {
        draft.altAnchor = "center";
      }
      const shape = buildShapeForDraft(draft, event, now);
      if (shape) {
        draft.points = shape.points;
        draft.primitive = shape.primitive;
        draft.pathDataOverride = shape.pathData;
        draft.shapeHoldStart = shape.holdStart;
        draft.shapeHoldOrigin = shape.holdOrigin;
      }
    } else {
      lineChainAnchor = null;
    }

    if (!draft.shapeModifiers) {
      if (shouldForceSegment) {
        const jitter = PEN_TAP_JITTER;
        appendPointWithInterpolation(
          draft.points,
          {
            x: firstPosition.x + jitter,
            y: firstPosition.y + jitter,
          },
          getPointerThresholds(draft.pointerType, draft.tool)
        );
      }

      if (positions.length > 1) {
        const thresholds = getPointerThresholds(draft.pointerType, draft.tool);
        for (let index = 1; index < positions.length; index += 1) {
          const position = positions[index]!;
          const next =
            draft.pointerType === "pen"
              ? smoothPenPoint(draft, position)
              : clonePoint(position);
          appendPointWithInterpolation(draft.points, next, thresholds);
        }
      }
    }

    activeStroke = draft;

    renderer.renderActiveStroke(
      createStrokePayload(
        draft,
        draft.pathDataOverride ??
          (draft.pointerType === "pen"
            ? pointsToSmoothPath(draft.points)
            : pointsToPath(draft.points))
      )
    );

    if (logDebug) {
      logger.debug("sketch", "▶️ Stroke started", {
        id: draft.id,
        pointerId: draft.pointerId,
        pointerType: draft.pointerType,
      });
    }
  };

  const updateStroke = (event: SketchPointerEvent): void => {
    if (!activeStroke || event.pointerId !== activeStroke.pointerId) {
      return;
    }

    const now = performance.now?.() ?? Date.now();
    const closedStroke =
      activeStroke.pointerType === "pen" &&
      isClosedStroke(activeStroke.points);
    if (closedStroke) {
      activeStroke.lineHoldStart = null;
      activeStroke.lineHoldOrigin = null;
      activeStroke.lineHoldTravel = null;
    }
    const lastPoint = activeStroke.points[activeStroke.points.length - 1];
    if (lastPoint) {
      const dx = event.position.x - lastPoint.x;
      const dy = event.position.y - lastPoint.y;
      if (dx * dx + dy * dy > END_HOLD_DRIFT * END_HOLD_DRIFT) {
        activeStroke.lastMoveAt = now;
      }
    }

    if (
      !activeStroke.shapeModifiers &&
      activeStroke.pointerType === "pen"
    ) {
      let holdStart = activeStroke.lineHoldStart ?? null;
      let holdOrigin = activeStroke.lineHoldOrigin ?? null;
      let holdTravel = activeStroke.lineHoldTravel ?? 0;
      if (!holdOrigin || holdStart === null) {
        holdOrigin = { x: event.position.x, y: event.position.y };
        holdStart = now;
        holdTravel = 0;
      } else {
        const dx = event.position.x - holdOrigin.x;
        const dy = event.position.y - holdOrigin.y;
        const driftSq = dx * dx + dy * dy;
        const lastPoint = activeStroke.points[activeStroke.points.length - 1];
        if (lastPoint) {
          const stepX = event.position.x - lastPoint.x;
          const stepY = event.position.y - lastPoint.y;
          holdTravel += Math.hypot(stepX, stepY);
        }
        if (
          driftSq > LINE_HOLD_MAX_DRIFT * LINE_HOLD_MAX_DRIFT ||
          holdTravel > LINE_HOLD_MAX_TRAVEL
        ) {
          holdStart = null;
          holdOrigin = null;
          holdTravel = 0;
        }
      }

      if (
        holdStart !== null &&
        now - holdStart >= LINE_HOLD_MS &&
        !closedStroke
      ) {
        activeStroke.shapeModifiers = { shift: true, alt: false };
        activeStroke.lineHoldStart = null;
        activeStroke.lineHoldOrigin = null;
        activeStroke.lineHoldTravel = null;
      } else {
        activeStroke.lineHoldStart = holdStart;
        activeStroke.lineHoldOrigin = holdOrigin;
        activeStroke.lineHoldTravel = holdStart ? holdTravel : null;
      }
    }

    if (!activeStroke.shapeModifiers) {
      const modifiers = resolveModifierState(event, config.getModifiers);
      if (modifiers.shift || modifiers.alt) {
        activeStroke.shapeModifiers = modifiers;
        if (modifiers.alt) {
          activeStroke.altAnchor = "corner";
        }
      }
    }
    if (activeStroke.shapeModifiers) {
      const modifiers = resolveModifierState(event, config.getModifiers);
      if (activeStroke.shapeModifiers.alt) {
        activeStroke.shapeModifiers = { shift: modifiers.shift, alt: true };
      } else if (modifiers.shift || modifiers.alt) {
        activeStroke.shapeModifiers = modifiers;
        if (modifiers.alt && !activeStroke.altAnchor) {
          activeStroke.altAnchor = "corner";
        }
      }
      const now = performance.now?.() ?? Date.now();
      const shape = buildShapeForDraft(activeStroke, event, now);
      if (shape) {
        activeStroke.points = shape.points;
        activeStroke.primitive = shape.primitive;
        activeStroke.pathDataOverride = shape.pathData;
        activeStroke.shapeHoldStart = shape.holdStart;
        activeStroke.shapeHoldOrigin = shape.holdOrigin;
      }
    } else {
      applyEventPositions(activeStroke, event);
    }

    activeStroke.updatedAt = performance.now?.() ?? Date.now();
    renderer.renderActiveStroke(
      createStrokePayload(
        activeStroke,
        activeStroke.pathDataOverride ??
          (activeStroke.pointerType === "pen"
            ? pointsToSmoothPath(activeStroke.points)
            : pointsToPath(activeStroke.points))
      )
    );
  };

  const finishStroke = (event: SketchPointerEvent): void => {
    if (!activeStroke) {
      if (logDebug) {
        console.warn(`🚨 TOOLMGR: finishStroke SKIPPED - no activeStroke`);
      }
      return;
    }
    if (event.pointerId !== activeStroke.pointerId) {
      if (logDebug) {
        console.warn(
          `🚨 TOOLMGR: finishStroke SKIPPED - pointerId mismatch (event=${event.pointerId}, stroke=${activeStroke.pointerId})`
        );
      }
      return;
    }

    const now = performance.now?.() ?? Date.now();
    let convertedClosedShape = false;
    if (
      !activeStroke.shapeModifiers &&
      activeStroke.pointerType === "pen" &&
      activeStroke.lastMoveAt !== null &&
      now - activeStroke.lastMoveAt >= END_HOLD_MS
    ) {
      const estimate = estimateClosedShape(activeStroke.points);
      if (estimate) {
        activeStroke.points = estimate.points;
        activeStroke.primitive = estimate.primitive;
        activeStroke.pathDataOverride = estimate.pathData;
        activeStroke.shapeHoldStart = null;
        activeStroke.shapeHoldOrigin = null;
        activeStroke.lineHoldStart = null;
        activeStroke.lineHoldOrigin = null;
        activeStroke.lineHoldTravel = null;
        convertedClosedShape = true;
      }
    }

    if (
      !activeStroke.shapeModifiers &&
      activeStroke.pointerType === "pen" &&
      activeStroke.lineHoldStart !== null &&
      (activeStroke.lineHoldTravel ?? 0) <= LINE_HOLD_MAX_TRAVEL &&
      !convertedClosedShape &&
      !isClosedStroke(activeStroke.points)
    ) {
      if (now - activeStroke.lineHoldStart >= LINE_HOLD_MS) {
        activeStroke.shapeModifiers = { shift: true, alt: false };
      }
      activeStroke.lineHoldStart = null;
      activeStroke.lineHoldOrigin = null;
      activeStroke.lineHoldTravel = null;
    }

    if (!activeStroke.shapeModifiers) {
      const modifiers = resolveModifierState(event, config.getModifiers);
      if (modifiers.shift || modifiers.alt) {
        activeStroke.shapeModifiers = modifiers;
        if (modifiers.alt) {
          activeStroke.altAnchor = "corner";
        }
      }
    }
    if (activeStroke.shapeModifiers) {
      const modifiers = resolveModifierState(event, config.getModifiers);
      if (activeStroke.shapeModifiers.alt) {
        activeStroke.shapeModifiers = { shift: modifiers.shift, alt: true };
      } else if (modifiers.shift || modifiers.alt) {
        activeStroke.shapeModifiers = modifiers;
        if (modifiers.alt && !activeStroke.altAnchor) {
          activeStroke.altAnchor = "corner";
        }
      }
      const now = performance.now?.() ?? Date.now();
      const shape = buildShapeForDraft(activeStroke, event, now);
      if (shape) {
        activeStroke.points = shape.points;
        activeStroke.primitive = shape.primitive;
        activeStroke.pathDataOverride = shape.pathData;
        activeStroke.shapeHoldStart = shape.holdStart;
        activeStroke.shapeHoldOrigin = shape.holdOrigin;
      }
    } else {
      applyEventPositions(activeStroke, event);
    }

    activeStroke.updatedAt = performance.now?.() ?? Date.now();

    const draft = activeStroke;
    activeStroke = null;
    renderer.renderActiveStroke(null);
    commitDraft(draft);
    lastStrokeEndTime = performance.now?.() ?? Date.now();
  };

  const handleEraserEvent = (event: SketchPointerEvent): void => {
    switch (event.phase) {
      case "down": {
        activeEraserPointerId = event.pointerId;
        renderer.renderActiveStroke(null);
        pendingEraserActions = [];
        eraserClearedRedo = false;
        eraserSourceStrokes = committedStrokes.map(cloneStroke);
        eraserPoints = [];
        const positions = getEventPositions(event);
        if (positions.length > 0) {
          eraserPoints.push(...positions);
        }
        const actions = eraseAtPositions(positions, {
          recordHistory: false,
          accumulate: pendingEraserActions,
          emitEvents: false,
        });
        if (actions.length > 0 && !eraserClearedRedo) {
          redoStack.length = 0;
          emitHistoryChange();
          eraserClearedRedo = true;
        }
        const last = positions[positions.length - 1] ?? event.position;
        updateEraserCursor(last);
        break;
      }
      case "move": {
        if (event.pointerId !== activeEraserPointerId) return;
        const positions = getEventPositions(event);
        if (positions.length > 0) {
          eraserPoints.push(...positions);
        }
        const actions = eraseAtPositions(positions, {
          recordHistory: false,
          accumulate: pendingEraserActions,
          emitEvents: false,
        });
        if (actions.length > 0 && !eraserClearedRedo) {
          redoStack.length = 0;
          emitHistoryChange();
          eraserClearedRedo = true;
        }
        const last = positions[positions.length - 1] ?? event.position;
        updateEraserCursor(last);
        break;
      }
      case "up":
      case "cancel": {
        if (event.pointerId !== activeEraserPointerId) return;
        const source = eraserSourceStrokes;
        if (source && eraserPoints.length > 0) {
          const { actions, nextStrokes } = computeEraserResult(
            source,
            eraserPoints
          );
          if (actions.length > 0) {
            committedStrokes.length = 0;
            renderer.reset();
            selectedStrokes = [];
            nextStrokes.forEach((stroke) => {
              applyStrokeAdd(stroke, {
                recordHistory: false,
                clearRedo: false,
                emitHistory: false,
                emitEvent: false,
              });
            });
            pushUndo({ type: "batch", actions });
            redoStack.length = 0;
            for (const action of actions) {
              if (action.type === "split") {
                config.onStrokeRemoved?.(action.stroke.id);
                action.segments.forEach((segment) => {
                  config.onStrokeAdded?.(segment);
                });
              } else if (action.type === "remove") {
                config.onStrokeRemoved?.(action.stroke.id);
              }
            }
            emitHistoryChange();
          }
        }
        pendingEraserActions = [];
        eraserClearedRedo = false;
        activeEraserPointerId = null;
        eraserSourceStrokes = null;
        eraserPoints = [];
        updateEraserCursor(null);
        break;
      }
      default:
        break;
    }
  };

  const handlePointerEvent = (event: SketchPointerEvent): void => {
    if (!isActive) {
      if (logDebug) {
        console.warn(
          `🚨 TOOLMGR: handlePointerEvent(${event.phase}) DROPPED - isActive=false`
        );
      }
      return;
    }

    if (currentStyle.tool === "eraser") {
      handleEraserEvent(event);
      return;
    }

    if (logDebug && (event.phase === "down" || event.phase === "up")) {
      logger.debug("sketch", "🎨 ToolManager event", {
        phase: event.phase,
        pointerId: event.pointerId,
        activeStroke: activeStroke?.id ?? null,
      });
    }

    switch (event.phase) {
      case "down":
        startStroke(event);
        break;
      case "move":
        updateStroke(event);
        break;
      case "up":
        finishStroke(event);
        break;
      case "cancel":
        finishStroke(event);
        break;
      default:
        break;
    }
  };

  const setActive = (value: boolean): void => {
    if (isActive === value) return;
    isActive = value;

    if (!isActive) {
      cancelActiveStroke();
      activeEraserPointerId = null;
      pendingEraserActions = [];
      eraserClearedRedo = false;
      updateEraserCursor(null);
    }
  };

  const reset = (): void => {
    activeStroke = null;
    activeEraserPointerId = null;
    pendingEraserActions = [];
    eraserClearedRedo = false;
    lastEraserPosition = null;
    selectedStrokes = [];
    committedStrokes.length = 0;
    undoStack.length = 0;
    redoStack.length = 0;
    renderer.reset();
    updateEraserCursor(null);
    emitHistoryChange();
  };

  const destroy = (): void => {
    reset();
  };

  const getStrokes = (): readonly SketchStrokeModel[] => committedStrokes;

  const setStrokes = (strokes: readonly SketchStrokeModel[]): void => {
    committedStrokes.length = 0;
    for (const stroke of strokes) {
      committedStrokes.push(stroke);
    }
    undoStack.length = 0;
    redoStack.length = 0;
    selectedStrokes = [];
    emitHistoryChange();
  };

  const ingestStroke = (stroke: SketchStrokeModel): void => {
    if (committedStrokes.some((existing) => existing.id === stroke.id)) {
      return;
    }
    applyStrokeAdd(stroke, {
      recordHistory: false,
      clearRedo: false,
      emitHistory: false,
      emitEvent: false,
    });
  };

  const removeStroke = (strokeId: string): void => {
    const index = committedStrokes.findIndex((stroke) => stroke.id === strokeId);
    if (index < 0) return;
    const stroke = committedStrokes[index]!;
    applyStrokeRemove(stroke, {
      recordHistory: false,
      clearRedo: false,
      emitHistory: false,
      indexOverride: index,
      emitEvent: false,
    });
    if (selectedStrokes.length > 0) {
      selectedStrokes = selectedStrokes.filter((item) => item.id !== strokeId);
    }
  };

  const removeStrokes = (strokeIds: readonly string[]): void => {
    if (!strokeIds.length) return;
    const toRemove = strokeIds
      .map((id) => {
        const index = committedStrokes.findIndex((stroke) => stroke.id === id);
        return index >= 0 ? { stroke: committedStrokes[index]!, index } : null;
      })
      .filter(
        (entry): entry is { stroke: SketchStrokeModel; index: number } =>
          Boolean(entry)
      )
      .sort((a, b) => b.index - a.index);
    if (!toRemove.length) return;

    const actions: HistoryAction[] = [];
    for (const entry of toRemove) {
      applyStrokeRemove(entry.stroke, {
        recordHistory: false,
        clearRedo: false,
        emitHistory: false,
        emitEvent: true,
        indexOverride: entry.index,
      });
      actions.push({
        type: "remove",
        stroke: entry.stroke,
        index: entry.index,
      });
    }
    selectedStrokes = selectedStrokes.filter(
      (stroke) => !strokeIds.includes(stroke.id)
    );
    pushUndo({ type: "batch", actions });
    redoStack.length = 0;
    emitHistoryChange();
  };

  const getSelection = (): readonly SketchStrokeModel[] => selectedStrokes;

  const setSelection = (selection: readonly SketchStrokeModel[]): void => {
    selectedStrokes = selection.slice();
  };

  const previewStrokeUpdates = (
    strokes: readonly SketchStrokeModel[]
  ): void => {
    for (const stroke of strokes) {
      renderer.updateStroke(createPayloadFromStroke(stroke));
    }
  };

  const commitStrokeUpdates = (
    before: readonly SketchStrokeModel[],
    after: readonly SketchStrokeModel[],
    options: { readonly emitEvents?: boolean } = {}
  ): void => {
    if (before.length === 0 || after.length === 0) return;
    const afterById = new Map<string, SketchStrokeModel>(
      after.map((stroke) => [stroke.id, stroke])
    );
    const actions: HistoryAction[] = [];
    const emitEvents = options.emitEvents !== false;

    for (const original of before) {
      const updated = afterById.get(original.id);
      if (!updated) continue;
      const index = committedStrokes.findIndex(
        (stroke) => stroke.id === updated.id
      );
      if (index < 0) continue;
      applyStrokeUpdate(original, updated, {
        recordHistory: false,
        clearRedo: false,
        emitHistory: false,
        emitEvent: emitEvents,
        indexOverride: index,
      });
      actions.push({
        type: "update",
        before: original,
        after: updated,
        index,
      });
    }

    if (actions.length > 0) {
      pushUndo({ type: "batch", actions });
      redoStack.length = 0;
      emitHistoryChange();
    }
  };

  const updateStrokes = (
    strokes: readonly SketchStrokeModel[]
  ): void => {
    if (strokes.length === 0) return;
    const updates = new Map(strokes.map((stroke) => [stroke.id, stroke]));
    for (let index = 0; index < committedStrokes.length; index += 1) {
      const existing = committedStrokes[index];
      if (!existing) continue;
      const next = updates.get(existing.id);
      if (!next) continue;
      applyStrokeUpdate(existing, next, {
        recordHistory: false,
        clearRedo: false,
        emitHistory: false,
        emitEvent: false,
        indexOverride: index,
      });
    }
  };

  const getStyle = (): SketchToolStyle => ({ ...currentStyle });

  const setStyle = (style: Partial<SketchToolStyle>): void => {
    applyStyleUpdate(style);
  };

  const applyHistoryActions = (
    actions: readonly HistoryAction[],
    mode: "undo" | "redo"
  ): SketchStrokeModel | null => {
    const ordered = mode === "undo" ? [...actions].reverse() : actions;
    let lastStroke: SketchStrokeModel | null = null;
    for (const action of ordered) {
      if (action.type === "add") {
        if (mode === "undo") {
          applyStrokeRemove(action.stroke, {
            recordHistory: false,
            clearRedo: false,
            emitHistory: false,
            indexOverride: action.index,
          });
        } else {
          applyStrokeAdd(action.stroke, {
            recordHistory: false,
            clearRedo: false,
            emitHistory: false,
            insertIndex: action.index,
          });
        }
        lastStroke = action.stroke;
      } else if (action.type === "remove") {
        if (mode === "undo") {
          applyStrokeAdd(action.stroke, {
            recordHistory: false,
            clearRedo: false,
            emitHistory: false,
            insertIndex: action.index,
          });
        } else {
          applyStrokeRemove(action.stroke, {
            recordHistory: false,
            clearRedo: false,
            emitHistory: false,
            indexOverride: action.index,
          });
        }
        lastStroke = action.stroke;
      } else if (action.type === "split") {
        const segments = action.segments;
        if (mode === "undo") {
          for (const segment of segments) {
            applyStrokeRemove(segment, {
              recordHistory: false,
              clearRedo: false,
              emitHistory: false,
            });
          }
          applyStrokeAdd(action.stroke, {
            recordHistory: false,
            clearRedo: false,
            emitHistory: false,
            insertIndex: action.index,
          });
        } else {
          applyStrokeRemove(action.stroke, {
            recordHistory: false,
            clearRedo: false,
            emitHistory: false,
            indexOverride: action.index,
          });
          segments.forEach((segment, offset) => {
            applyStrokeAdd(segment, {
              recordHistory: false,
              clearRedo: false,
              emitHistory: false,
              insertIndex: action.index + offset,
            });
          });
        }
        lastStroke = action.stroke;
      } else if (action.type === "update") {
        const target = mode === "undo" ? action.before : action.after;
        applyStrokeUpdate(action.before, target, {
          recordHistory: false,
          clearRedo: false,
          emitHistory: false,
          indexOverride: action.index,
        });
        lastStroke = target;
      }
    }
    return lastStroke;
  };

  const undo = (): SketchStrokeModel | null => {
    const entry = undoStack.pop();
    if (!entry) return null;
    const stroke = applyHistoryActions(entry.actions, "undo");
    redoStack.push(entry);
    emitHistoryChange();
    return stroke;
  };

  const redo = (): SketchStrokeModel | null => {
    const entry = redoStack.pop();
    if (!entry) return null;
    const stroke = applyHistoryActions(entry.actions, "redo");
    pushUndo(entry);
    emitHistoryChange();
    return stroke;
  };

  const canUndo = (): boolean => undoStack.length > 0;

  const canRedo = (): boolean => redoStack.length > 0;

  /**
   * Force-finish any active stroke for the given pointer ID.
   * Used when cleaning up orphaned pointers during rapid stroke succession.
   */
  const forceFinishStroke = (pointerId: number): void => {
    if (!activeStroke) return;

    // Only finish if the pointer ID matches (or if we're cleaning up any orphan)
    if (activeStroke.pointerId !== pointerId) return;

    if (logDebug) {
      logger.debug("sketch", "⏩ Force-finishing orphaned stroke", {
        id: activeStroke.id,
        pointerId: activeStroke.pointerId,
        points: activeStroke.points.length,
      });
    }

    activeStroke.updatedAt = performance.now?.() ?? Date.now();

    const draft = activeStroke;
    activeStroke = null;
    renderer.renderActiveStroke(null);
    commitDraft(draft);
    lastStrokeEndTime = performance.now?.() ?? Date.now();
  };

  return {
    handlePointerEvent,
    setActive,
    reset,
    destroy,
    getStrokes,
    setStrokes,
    ingestStroke,
    removeStroke,
    removeStrokes,
    getSelection,
    setSelection,
    previewStrokeUpdates,
    commitStrokeUpdates,
    updateStrokes,
    getStyle,
    setStyle,
    undo,
    redo,
    canUndo,
    canRedo,
    forceFinishStroke,
  };
};

const createStrokeId = (): string => {
  if (
    typeof globalThis !== "undefined" &&
    globalThis.crypto &&
    "randomUUID" in globalThis.crypto
  ) {
    return globalThis.crypto.randomUUID();
  }

  const random = Math.random().toString(36).slice(2, 8);
  return `stroke-${Date.now().toString(36)}-${random}`;
};
